import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { timelineApi, api } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Button, Flex, Select, Input, Spinner, LoadingContainer } from '../../components/shared/StyledComponents'
import { TimelineChart } from './components/TimelineChart'
import { TeamChart } from './components/TeamChart'
import { PersonBreakdownToast } from './components/PersonBreakdownToast'
import { OpportunityTeamToast } from './components/OpportunityTeamToast'
import { OpportunitySummaryModal } from './components/OpportunitySummaryModal'
import { isPracticas, resolveWindow, windowOverlaps } from './loadRules'

const PageTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  font-size: ${({ theme }) => theme.fontSizes.xl};
`

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`

const ViewTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`

interface ViewTabProps {
    active?: boolean
}

const ViewTab = styled.button<ViewTabProps>`
  padding: 0.75rem 1.5rem;
  background: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.secondary};
  color: ${({ active }) => active ? 'white' : 'inherit'};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primary : '#d0dce0'};
  }
`

const DateControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  background: white;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ControlLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
`

const SmallSelect = styled(Select)`
  width: auto;
  min-width: 100px;
`

const SmallInput = styled(Input)`
  width: auto;
`

const WorkloadSummary = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`

const WorkloadCard = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  min-width: 120px;
`

const WorkloadValue = styled.div<{ color?: string }>`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 700;
  color: ${({ color }) => color || 'inherit'};
`

const WorkloadLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const Legend = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  flex-wrap: wrap;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const LegendColor = styled.div<{ color: string }>`
  width: 20px;
  height: 12px;
  border-radius: 3px;
  background: ${({ color }) => color};
`

const IntensityLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-left: ${({ theme }) => theme.spacing.lg};
  padding-left: ${({ theme }) => theme.spacing.lg};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
`

const IntensityBar = styled.div<{ opacity: number; color: string }>`
  width: 30px;
  height: 12px;
  border-radius: 3px;
  background: ${({ color }) => color};
  opacity: ${({ opacity }) => opacity};
`

export const TimelinePage: React.FC = () => {
    const { user } = useAuth()
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const [view, setView] = useState<'projects' | 'team'>('projects')
    const [granularity, setGranularity] = useState<'days' | 'weeks' | 'months'>('months')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [projectsData, setProjectsData] = useState<any>(null)
    const [teamData, setTeamData] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedPerson, setSelectedPerson] = useState<any>(null)
    const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null)
    const [summaryModalOpportunityId, setSummaryModalOpportunityId] = useState<number | null>(null)
    const [opportunityPhaseMeta, setOpportunityPhaseMeta] = useState<Record<string, {
        ofertaEstado?: string
        proyectoEstado?: string
    }>>({})
    const [opportunityTeamsById, setOpportunityTeamsById] = useState<Record<string, {
        oferta: Array<{ userId: number | string; displayName: string; load: number }>
        proyecto: Array<{ userId: number | string; displayName: string; load: number }>
    }>>({})
    const [isExporting, setIsExporting] = useState(false)
    const [refreshKey, setRefreshKey] = useState(0)

    // Reload data every time the component mounts (when user navigates to Timeline)
    useEffect(() => {
        initDates()
        // Force refresh on mount
        setRefreshKey(prev => prev + 1)
    }, [])

    useEffect(() => {
        if (dateFrom && dateTo) {
            loadData()
        }
    }, [dateFrom, dateTo, refreshKey])

    const initDates = () => {
        const today = new Date()
        const from = new Date(today)
        const to = new Date(today)

        // Default to 3 months view
        to.setMonth(to.getMonth() + 3)

        setDateFrom(formatDateInput(from))
        setDateTo(formatDateInput(to))
    }

    const formatDateInput = (date: Date) => date.toISOString().split('T')[0]

    const loadData = async () => {
        setIsLoading(true)
        try {
            // Load timeline data and users data.
            // /timeline/team carries the new per-user `assignments[]` array
            // (LinkedIn learning tasks) — only needed/allowed for admins.
            const [projectsRes, usersRes, opportunitiesRes, teamRes] = await Promise.all([
                timelineApi.getProjects(dateFrom, dateTo),
                api.users.getAll(),
                api.opportunities.getAll(),
                isAdmin
                    ? timelineApi.getTeam(dateFrom, dateTo).catch(() => null)
                    : Promise.resolve(null),
            ])

            setProjectsData(projectsRes.data)

            // Build team data from users and opportunities
            const usersData = Array.isArray(usersRes.data) ? usersRes.data : []
            const oppsData = Array.isArray(opportunitiesRes.data) ? opportunitiesRes.data : []

            // Map of user_id → LinkedIn assignments[] from the /timeline/team response.
            const assignmentsByUser: Record<string, any[]> = {}
            const backendTeam = teamRes?.data?.team
            if (Array.isArray(backendTeam)) {
                backendTeam.forEach((m: any) => {
                    const uid = String(m.user_id ?? m.userId)
                    if (Array.isArray(m.assignments)) {
                        assignmentsByUser[uid] = m.assignments
                    }
                })
            }

            // Keep offer/project state metadata by opportunity id to resolve visible phase on click.
            const phaseMeta: Record<string, { ofertaEstado?: string; proyectoEstado?: string }> = {}
            const teamsById: Record<string, {
                oferta: Array<{ userId: number | string; displayName: string; load: number }>
                proyecto: Array<{ userId: number | string; displayName: string; load: number }>
            }> = {}

            const mapTeam = (team: any[] | undefined) => {
                if (!Array.isArray(team)) return []
                return team.map((member: any) => ({
                    userId: member.userId || member.user_id,
                    displayName: member.userName || member.display_name || member.username || 'Usuario',
                    load: member.load ?? member.capacity_pct ?? 0,
                }))
            }

            oppsData.forEach((opp: any) => {
                const id = String(opp.id)
                phaseMeta[id] = {
                    ofertaEstado: opp.oferta?.estado,
                    proyectoEstado: opp.proyecto?.estado,
                }
                teamsById[id] = {
                    oferta: mapTeam(opp.oferta?.equipo),
                    proyecto: mapTeam(opp.proyecto?.equipo),
                }
            })
            setOpportunityPhaseMeta(phaseMeta)
            setOpportunityTeamsById(teamsById)

            const today = new Date()
            today.setHours(0, 0, 0, 0)

            // T&M projects without end date extend to 2099
            const getEffectiveProyectoFin = (opp: any): string | null | undefined => {
                if (!opp.proyecto?.fecha_fin && opp.general?.time_material && opp.proyecto?.fecha_inicio) {
                    return '2099-12-31'
                }
                return opp.proyecto?.fecha_fin
            }

            // Filter out admin users by username
            const teamMembers = usersData
                .filter((u: any) => {
                    const username = (u.username || '').toLowerCase()
                    return username !== 'admin'
                })
                .map((user: any) => {
                    // Find opportunities assigned to this user
                    const userOpportunities: any[] = []
                    let todayCapacity = 0

                    oppsData.forEach((opp: any) => {
                        // Check oferta team and proyecto team separately
                        const ofertaTeam = opp.oferta?.equipo || []
                        const proyectoTeam = opp.proyecto?.equipo || []

                        // Find assignment in oferta team
                        const ofertaAssignment = ofertaTeam.find((t: any) =>
                            String(t.userId || t.user_id) === String(user.id)
                        )

                        // Find assignment in proyecto team
                        const proyectoAssignment = proyectoTeam.find((t: any) =>
                            String(t.userId || t.user_id) === String(user.id)
                        )

                        // Process oferta assignment if exists
                        // Only count ofertas in active states (matching backend logic)
                        // Skip oferta if user is also on the active proyecto team for same opportunity
                        // (deduplication: proyecto supersedes oferta when both exist and proyecto is active)
                        const ofertaEstado = (opp.oferta?.estado || '').toLowerCase()
                        const ofertaActiveStates = ['ganada', 'borrador', 'en_preparacion', 'en_revision']
                        const proyectoEstadoForDedup = (opp.proyecto?.estado || '').toLowerCase()
                        const hasActiveProyecto = proyectoAssignment && ['en_curso', 'planificacion'].includes(proyectoEstadoForDedup)
                        if (ofertaAssignment && ofertaActiveStates.includes(ofertaEstado) && !hasActiveProyecto) {
                            const load = ofertaAssignment.load || ofertaAssignment.capacity_pct || 0

                            // Per-member assignment dates (opportunity_users.fecha_inicio/fecha_fin)
                            // already honour the role's start_availability offset (A6).
                            // Prefer them over the opportunity-level oferta dates so a deferred-
                            // start role renders its bar at the correct offset.
                            const assignStartStr = ofertaAssignment.fecha_inicio || opp.oferta?.fecha_inicio
                            const assignEndStr = ofertaAssignment.fecha_fin || opp.oferta?.fecha_entrega
                            // No dates = skipped; start-only = open-ended (shared load rules).
                            const loadWindow = resolveWindow(assignStartStr, assignEndStr)
                            const name = opp.general?.name || opp.name || 'Sin nombre'
                            const practicas = isPracticas(name)

                            // Only list opportunities active from today onwards
                            if (loadWindow && loadWindow.end >= today) {
                                userOpportunities.push({
                                    id: opp.id,
                                    name,
                                    capacity_pct: load,
                                    oferta_fecha_inicio: assignStartStr,
                                    oferta_fecha_entrega: assignEndStr,
                                    proyecto_fecha_inicio: null,
                                    proyecto_fecha_fin: null,
                                    type: 'oferta' as const,
                                    is_practicas: practicas,
                                })
                            }
                            if (!practicas && windowOverlaps(loadWindow, today, today)) {
                                todayCapacity += load
                            }
                        }

                        // Process proyecto assignment if exists
                        // Only count proyectos in active states (matching backend logic)
                        const proyectoEstado = (opp.proyecto?.estado || '').toLowerCase()
                        const proyectoActiveStates = ['en_curso', 'planificacion']
                        if (proyectoAssignment && proyectoActiveStates.includes(proyectoEstado)) {
                            const load = proyectoAssignment.load || proyectoAssignment.capacity_pct || 0

                            // Prefer per-member assignment dates (A6 shifted fecha_inicio) over
                            // opportunity-level proyecto dates.
                            const effectiveProyectoFin = getEffectiveProyectoFin(opp)
                            const assignStartStr = proyectoAssignment.fecha_inicio || opp.proyecto?.fecha_inicio
                            const assignEndStr = proyectoAssignment.fecha_fin || effectiveProyectoFin
                            // No dates = skipped; start-only = open-ended (shared load rules).
                            const loadWindow = resolveWindow(assignStartStr, assignEndStr)
                            const name = opp.general?.name || opp.name || 'Sin nombre'
                            const practicas = isPracticas(name)

                            // Only list opportunities active from today onwards
                            if (loadWindow && loadWindow.end >= today) {
                                userOpportunities.push({
                                    id: opp.id,
                                    name,
                                    capacity_pct: load,
                                    oferta_fecha_inicio: null,
                                    oferta_fecha_entrega: null,
                                    proyecto_fecha_inicio: assignStartStr,
                                    proyecto_fecha_fin: assignEndStr,
                                    type: 'proyecto' as const,
                                    is_practicas: practicas,
                                })
                            }
                            // The prácticas slot never adds to load (matrix/dashboard/recommender agree).
                            if (!practicas && windowOverlaps(loadWindow, today, today)) {
                                todayCapacity += load
                            }
                        }
                    })

                    // Compute future max capacity: peak load at any future change point
                    // Collect all future start/end dates from this user's opportunities
                    const futureChangePoints: Date[] = []
                    userOpportunities.forEach((opp: any) => {
                        const startStr = opp.oferta_fecha_inicio || opp.proyecto_fecha_inicio
                        const endStr = opp.proyecto_fecha_fin || opp.oferta_fecha_entrega
                        if (startStr) {
                            const d = new Date(startStr); d.setHours(0, 0, 0, 0)
                            if (d > today) futureChangePoints.push(d)
                        }
                        if (endStr) {
                            const d = new Date(endStr); d.setHours(0, 0, 0, 0)
                            if (d > today) futureChangePoints.push(d)
                            const dayAfter = new Date(d); dayAfter.setDate(dayAfter.getDate() + 1)
                            if (dayAfter > today) futureChangePoints.push(dayAfter)
                        }
                    })
                    // Deduplicate and sort
                    const uniqueFuturePoints = [...new Set(futureChangePoints.map(d => d.getTime()))]
                        .sort((a, b) => a - b)
                        .map(t => new Date(t))

                    let futureMaxCapacity = todayCapacity
                    uniqueFuturePoints.forEach(point => {
                        let loadAtPoint = 0
                        userOpportunities.forEach((opp: any) => {
                            if (opp.is_practicas) return
                            const startStr = opp.oferta_fecha_inicio || opp.proyecto_fecha_inicio
                            const endStr = opp.proyecto_fecha_fin || opp.oferta_fecha_entrega
                            if (windowOverlaps(resolveWindow(startStr, endStr), point, point)) {
                                loadAtPoint += opp.capacity_pct || 0
                            }
                        })
                        if (loadAtPoint > futureMaxCapacity) futureMaxCapacity = loadAtPoint
                    })

                    return {
                        user_id: user.id,
                        display_name: user.display_name || user.username || 'Usuario',
                        total_capacity: todayCapacity,
                        today_capacity: todayCapacity,
                        future_max_capacity: futureMaxCapacity,
                        opportunities: userOpportunities,
                        // LinkedIn learning tasks rendered as a separate purple lane.
                        // Capacity is intentionally NOT folded into the load/overload math.
                        assignments: assignmentsByUser[String(user.id)] || [],
                    }
                })

            setTeamData({ team: teamMembers })
        } catch (error) {
            console.error('Error loading timeline data:', error)
        }
        setIsLoading(false)
    }

    const handleGranularityChange = (newGranularity: 'days' | 'weeks' | 'months') => {
        setGranularity(newGranularity)

        // Adjust date range based on granularity
        const from = new Date(dateFrom)
        const to = new Date(from)

        switch (newGranularity) {
            case 'days':
                to.setDate(to.getDate() + 14) // 2 weeks in days
                break
            case 'weeks':
                to.setDate(to.getDate() + 8 * 7) // 8 weeks
                break
            case 'months':
            default:
                to.setMonth(to.getMonth() + 3) // 3 months
                break
        }

        setDateTo(formatDateInput(to))
    }

    const resetDates = () => {
        setGranularity('months')
        initDates()
    }

    const calculateWorkloadCounts = () => {
        if (!teamData?.team) return { unassigned: 0, normal: 0, busy: 0, overloaded: 0, futureOverloaded: 0 }

        const counts = { unassigned: 0, normal: 0, busy: 0, overloaded: 0, futureOverloaded: 0 }
        teamData.team.forEach((member: any) => {
            const cap = member.today_capacity ?? member.total_capacity ?? 0
            if (cap === 0) counts.unassigned++
            // Exactly 50% is 'normal', as the '≤50%' label and Estadísticas say
            else if (cap <= 50) counts.normal++
            else if (cap <= 80) counts.busy++
            else counts.overloaded++
            // Count people with future overload (>100%) who are not currently overloaded
            const futureCap = member.future_max_capacity ?? cap
            if (futureCap > 100 && cap <= 100) counts.futureOverloaded++
        })
        return counts
    }

    const workloadCounts = calculateWorkloadCounts()

    const toDayStart = (value: string | null | undefined): Date | null => {
        if (!value) return null
        const parsed = new Date(value)
        if (Number.isNaN(parsed.getTime())) return null
        parsed.setHours(0, 0, 0, 0)
        return parsed
    }

    const resolveOpportunityPhase = (project: any): 'oferta' | 'proyecto' => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        const ofertaStart = toDayStart(project.oferta_fecha_inicio)
        const ofertaEnd = toDayStart(project.oferta_fecha_entrega)
        const proyectoStart = toDayStart(project.proyecto_fecha_inicio)

        // Date-first rule requested: once offer ends, always show project team.
        if (ofertaEnd && today > ofertaEnd) {
            return 'proyecto'
        }

        // While offer is active (or until end date), show offer team.
        if (ofertaEnd && today <= ofertaEnd) {
            return 'oferta'
        }

        const meta = opportunityPhaseMeta[String(project.id)] || {}
        const ofertaEstado = (meta.ofertaEstado || '').toLowerCase()
        const proyectoEstado = (meta.proyectoEstado || '').toLowerCase()

        if (ofertaEstado === 'ganada') {
            return 'proyecto'
        }

        if (['planificacion', 'en_curso', 'pausado', 'completado', 'mantenimiento'].includes(proyectoEstado)) {
            return 'proyecto'
        }

        // Fallbacks when offer end is missing.
        if (ofertaStart && (!proyectoStart || today < proyectoStart)) {
            return 'oferta'
        }

        if (proyectoStart) {
            return 'proyecto'
        }

        return 'oferta'
    }

    const handlePersonClick = (member: any) => {
        setSelectedPerson(member)
    }

    const handleOpportunityClick = (project: any) => {
        setSummaryModalOpportunityId(project.id)
    }

    const handleOpportunityNameClick = (project: any) => {
        const id = String(project.id)
        const phase = resolveOpportunityPhase(project)
        const teams = opportunityTeamsById[id]
        const team = phase === 'proyecto' ? (teams?.proyecto || []) : (teams?.oferta || [])

        setSelectedOpportunity({
            id: project.id,
            name: project.name,
            type: phase,
            team: team,
        })
    }

    const handleExport = async () => {
        if (!dateFrom || !dateTo) return
        setIsExporting(true)
        try {
            const response = view === 'projects'
                ? await timelineApi.exportProjects(dateFrom, dateTo)
                : await timelineApi.exportTeam(dateFrom, dateTo)

            // Create download link
            const blob = new Blob([response.data], {
                type: 'application/pdf'
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            const today = new Date().toISOString().split('T')[0]
            link.download = view === 'projects'
                ? `timeline_projects_${today}.pdf`
                : `team_workload_${today}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Error exporting data:', error)
            alert('Error al exportar los datos')
        }
        setIsExporting(false)
    }

    return (
        <div>
            <PageTitle>Timeline de Oportunidades</PageTitle>

            <Controls>
                <ViewTabs>
                    <ViewTab active={view === 'projects'} onClick={() => setView('projects')}>
                        📊 Oportunidades
                    </ViewTab>
                    {isAdmin && (
                        <ViewTab active={view === 'team'} onClick={() => setView('team')}>
                            👥 Carga de Equipo
                        </ViewTab>
                    )}
                </ViewTabs>

                <DateControls>
                    <ControlGroup>
                        <ControlLabel>Granularidad</ControlLabel>
                        <SmallSelect
                            value={granularity}
                            onChange={(e) => handleGranularityChange(e.target.value as any)}
                        >
                            <option value="days">Días</option>
                            <option value="weeks">Semanas</option>
                            <option value="months">Meses</option>
                        </SmallSelect>
                    </ControlGroup>
                    <ControlGroup>
                        <ControlLabel>Desde</ControlLabel>
                        <SmallInput
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                    </ControlGroup>
                    <ControlGroup>
                        <ControlLabel>Hasta</ControlLabel>
                        <SmallInput
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </ControlGroup>
                    <Button variant="secondary" size="sm" onClick={resetDates} style={{ alignSelf: 'flex-end' }}>
                        Restablecer
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleExport}
                        disabled={isExporting || isLoading}
                        style={{ alignSelf: 'flex-end' }}
                    >
                        {isExporting ? '⏳ Exportando...' : '📥 Exportar PDF'}
                    </Button>
                </DateControls>
            </Controls>

            {view === 'team' && (
                <WorkloadSummary>
                    <WorkloadCard>
                        <WorkloadValue color="#FFC107">{workloadCounts.unassigned}</WorkloadValue>
                        <WorkloadLabel>Sin asignar (hoy)</WorkloadLabel>
                    </WorkloadCard>
                    <WorkloadCard>
                        <WorkloadValue color="#4CAF50">{workloadCounts.normal}</WorkloadValue>
                        <WorkloadLabel>Carga normal (≤50%)</WorkloadLabel>
                    </WorkloadCard>
                    <WorkloadCard>
                        <WorkloadValue color="#FF9800">{workloadCounts.busy}</WorkloadValue>
                        <WorkloadLabel>Ocupados (51-80%)</WorkloadLabel>
                    </WorkloadCard>
                    <WorkloadCard>
                        <WorkloadValue color="#F44336">{workloadCounts.overloaded}</WorkloadValue>
                        <WorkloadLabel>Sobrecargados (&gt;80%)</WorkloadLabel>
                    </WorkloadCard>
                    {workloadCounts.futureOverloaded > 0 && (
                        <WorkloadCard style={{ border: '2px solid #FF9800', background: '#FFF3E0' }}>
                            <WorkloadValue color="#FF9800">{workloadCounts.futureOverloaded}</WorkloadValue>
                            <WorkloadLabel>⚠️ Futura sobrecarga (&gt;100%)</WorkloadLabel>
                        </WorkloadCard>
                    )}
                </WorkloadSummary>
            )}

            {isLoading ? (
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            ) : view === 'projects' ? (
                <TimelineChart
                    data={projectsData}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    granularity={granularity}
                    onOpportunityClick={handleOpportunityClick}
                    onOpportunityNameClick={handleOpportunityNameClick}
                />
            ) : (
                <TeamChart
                    data={teamData}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    granularity={granularity}
                    onPersonClick={handlePersonClick}
                />
            )}

            <Legend>
                {view === 'projects' ? (
                    <>
                        <LegendItem>
                            <LegendColor color="#2196F3" />
                            <span>📋 Oferta</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#4CAF50" />
                            <span>🚀 Proyecto</span>
                        </LegendItem>
                    </>
                ) : (
                    <>
                        <LegendItem>
                            <LegendColor color="#4CAF50" />
                            <span>Carga baja (≤50%)</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#FF9800" />
                            <span>Carga media (51-80%)</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#F44336" />
                            <span>Carga alta (&gt;80%)</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#9C27B0" />
                            <span>🎓 Tarea LinkedIn</span>
                        </LegendItem>
                        <IntensityLegend>
                            <span style={{ fontSize: '0.7rem', marginRight: '8px' }}>Intensidad (dedicación):</span>
                            <IntensityBar opacity={0.5} color="#666" />
                            <span style={{ fontSize: '0.65rem' }}>Baja</span>
                            <IntensityBar opacity={0.8} color="#666" />
                            <span style={{ fontSize: '0.65rem' }}>Media</span>
                            <IntensityBar opacity={1} color="#666" />
                            <span style={{ fontSize: '0.65rem' }}>Alta</span>
                        </IntensityLegend>
                    </>
                )}
            </Legend>

            {selectedPerson && (
                <PersonBreakdownToast
                    person={selectedPerson}
                    onClose={() => setSelectedPerson(null)}
                    onViewDetails={(oppId) => {
                        setSelectedPerson(null)
                        setSummaryModalOpportunityId(oppId)
                    }}
                />
            )}

            {selectedOpportunity && (
                <OpportunityTeamToast
                    opportunity={selectedOpportunity}
                    onClose={() => setSelectedOpportunity(null)}
                    isAdmin={isAdmin}
                    onViewDetails={(oppId) => {
                        setSelectedOpportunity(null)
                        setSummaryModalOpportunityId(oppId)
                    }}
                />
            )}

            {summaryModalOpportunityId && (
                <OpportunitySummaryModal
                    opportunityId={summaryModalOpportunityId}
                    onClose={() => setSummaryModalOpportunityId(null)}
                    isAdmin={isAdmin}
                />
            )}
        </div>
    )
}
